import { ArrowLeft, CalendarClock, ExternalLink, FolderOpen, Mail, Phone, Wallet } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { api } from "@/api";
import { CallLink } from "@/components/CallLink";
import { LeadAttachments } from "@/components/pipeline/LeadAttachments";
import { timelineItems } from "@/components/pipeline/LeadTimeline";
import { LeadHistory } from "@/components/pipeline/lead/LeadHistory";
import { frDate, initials } from "@/components/pipeline/utils";
import { ClientSav, ClientTasks } from "@/components/clients/ClientActions";
import { PhoneCard } from "@/components/pipeline/lead/PhoneCard";
import { mountInline, unmountInline } from "@/lib/ringoverPhone";
import "@/components/pipeline/lead/lead.css";
import { Amount, SavBadge, STAGE_TONE, place } from "@/components/clients/ClientBits";
import { enterPage } from "@/themes/rapport/motion";

const Ring = ({ pct, tone, label }) => (
  <div className="pc__photo"><svg className="pc__ring" viewBox="0 0 160 160" aria-hidden="true"><circle className="pc__track" cx="80" cy="80" r="74" /><circle className={`pc__arc pc__arc--${tone}`} cx="80" cy="80" r="74" strokeDasharray={`${(pct / 100) * 465} 465`} /></svg>
    <span className="av cl-av" aria-hidden="true">{label}</span><span className="pc__health" data-testid="client-progress">{pct}%</span></div>
);

const Stat = ({ icon: Icon, value, label, testid, tone }) => (
  <div className="stat" data-testid={testid}><span className="stat__ico" aria-hidden="true"><Icon size={18} /></span><div><p className={`stat__value ${tone || ""}`}>{value}</p><p className="stat__label">{label}</p></div></div>
);

const nextLabel = (c) => (c.next_task ? `${frDate(c.next_task.remind_date)}${c.next_task.remind_time ? ` · ${c.next_task.remind_time}` : ""}` : c.late_tasks ? `${c.late_tasks} en retard` : "Aucune");

// Frise après-vente d'un dossier : devis signé → VT → installation → dossier CEE → prime versée
const Frise = ({ d, steps }) => (
  <li className="cl-dossier" data-testid={`client-dossier-${d.lead_id}`}>
    <p className="cl-dossier__head"><Link to={`/pipeline?lead=${d.lead_id}`} data-testid={`client-dossier-open-${d.lead_id}`}>{d.title} <ExternalLink size={12} /></Link><b><Amount value={d.amount} testid={`client-dossier-amount-${d.lead_id}`} /></b></p>
    <small>{d.pipeline} · {d.step}</small>
    <ol className="cl-frise">{steps.map((s, i) => <li key={s.id} className={i < d.stage_index ? "is-done" : i === d.stage_index ? "is-now" : ""} data-testid={`client-frise-${d.lead_id}-${s.id}`}><i />{s.label}</li>)}</ol>
  </li>
);

const FriseList = ({ dossiers, steps }) => {
  const [all, setAll] = useState(false);
  return (
    <>
      <ol className="cl-dossiers">{(all ? dossiers : dossiers.slice(0, 4)).map((d) => <Frise key={d.lead_id} d={d} steps={steps} />)}</ol>
      {dossiers.length > 4 && <button type="button" className="link-button cl-more" onClick={() => setAll(!all)} data-testid="client-frise-more">{all ? "Réduire" : `Voir les ${dossiers.length - 4} autres dossiers`}</button>}
    </>
  );
};

// Un seul bloc Documents : pièces jointes du dossier choisi
const ClientDocs = ({ dossiers }) => {
  const [lead, setLead] = useState(dossiers[0]?.lead_id);
  if (!dossiers.length) return null;
  return (
    <section className="card cxp__det cl-docsbox" data-testid="client-documents">
      {dossiers.length > 1 && <select className="pl-select" value={lead} onChange={(e) => setLead(Number(e.target.value))} data-testid="client-documents-dossier">{dossiers.map((d) => <option key={d.lead_id} value={d.lead_id}>{d.title}</option>)}</select>}
      <LeadAttachments key={lead} leadId={lead} />
    </section>
  );
};

// Téléphone Ringover (colonne de droite), réduit hors appel comme sur la fiche prospect
const ClientPhone = ({ phone }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => { mountInline("pl-sheet-phone-frame").catch(() => {}); return () => unmountInline(); }, []);
  return <PhoneCard ready open={open} setOpen={setOpen} phone={phone} />;
};

export default function ClientPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [leads, setLeads] = useState([]);
  const ref = useRef(null);
  useEffect(() => {
    setC(null);
    api.get(`/clients/${id}`).then(({ data }) => {
      setC(data);
      Promise.all(data.dossiers_list.slice(0, 6).map((d) => api.get(`/pipeline/leads/${d.lead_id}`).then((r) => r.data).catch(() => null))).then((l) => setLeads(l.filter(Boolean)));
    }).catch(() => setC(false));
  }, [id]);
  useEffect(() => (c ? enterPage(ref.current) : undefined), [Boolean(c)]); // eslint-disable-line react-hooks/exhaustive-deps
  if (c === false) return <section className="view" data-view="customers"><p className="card pl-empty" data-testid="client-not-found">Client introuvable. <Link to="/clients">Retour aux clients</Link></p></section>;
  if (!c) return <section className="view" data-view="customers" data-testid="client-loading"><div className="card ldp-skel" style={{ height: 420 }} /></section>;
  const pct = Math.round(Math.max(0, ["signed", "vt", "install", "cee", "paid", "done"].indexOf(c.stage)) / c.aftersale.length * 100);
  const items = leads.flatMap((l) => timelineItems(l.comments, l.communications).map((i) => ({ ...i, title: c.dossiers > 1 && i.kind !== "comment" ? `${i.title} · ${l.lead.title}` : i.title }))).sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  return (
    <section className="view cl-page" data-view="customers" ref={ref} data-testid="client-page">
      <header className="head"><div className="head__lead"><button type="button" className="ibtn magnetic" onClick={() => navigate("/clients")} aria-label="Retour aux clients" data-testid="client-back"><ArrowLeft size={18} /></button><h1 className="head__title" data-split="" data-testid="client-title">{c.name}</h1></div>
        <div className="head__stats" data-testid="client-head-stats">
          <Stat icon={Wallet} value={<Amount value={c.amount} />} label="CA signé" testid="client-stat-amount" />
          <Stat icon={FolderOpen} value={c.dossiers} label={`dossier${c.dossiers > 1 ? "s" : ""} signé${c.dossiers > 1 ? "s" : ""}`} testid="client-stat-dossiers" />
          <Stat icon={CalendarClock} value={nextLabel(c)} label="prochaine tâche" testid="client-stat-next" tone={!c.next_task && c.late_tasks ? "cl-missing" : ""} />
        </div></header>
      <div className="cxp cl-cols">
        <div className="cl-col" data-testid="client-col-left">
        <aside className="card cxp__prof" data-testid="client-profile">
          <Ring pct={pct} tone={STAGE_TONE[c.stage]} label={initials(c.name)} />
          <h2 className="pc__name" data-testid="client-name">{c.name}</h2>
          <p className="pc__role">{place(c) ? `${place(c)} · ` : ""}client depuis le {frDate(c.won_at)}</p>
          <div className="pc__meta">{c.pipelines.map((p) => <span key={p} className="chip">{p}</span>)}<SavBadge sav={c.sav} testid="client-sav-badge" /></div>
          <div className="pc__acts">
            {c.phones[0] && <CallLink phone={c.phones[0]} className="ibtn" testid="client-call"><Phone size={16} /></CallLink>}
            {c.emails[0] && <a className="ibtn" href={`mailto:${c.emails[0]}`} aria-label="Écrire" data-testid="client-mail"><Mail size={16} /></a>}
          </div>
          <dl className="cl-facts"><div><dt>Adresse</dt><dd data-testid="client-address">{c.address || "—"}</dd></div><div><dt>Ville</dt><dd data-testid="client-city">{place(c) || "—"}</dd></div><div><dt>Produit(s)</dt><dd data-testid="client-products">{(c.products || []).join(", ") || "—"}</dd></div><div><dt>CA signé</dt><dd data-testid="client-amount"><Amount value={c.amount} /></dd></div><div><dt>Commercial</dt><dd>{c.owner_name || "—"}</dd></div><div><dt>Téléphone</dt><dd>{c.phones.join(", ") || "—"}</dd></div><div><dt>E-mail</dt><dd>{c.emails.join(", ") || "—"}</dd></div></dl>
        </aside>
        <ClientDocs dossiers={c.dossiers_list} />
        </div>
        <div className="cl-col" data-testid="client-col-center">
          <div className="cxp__hist"><LeadHistory items={items} /></div>
        </div>
        <div className="cl-col" data-testid="client-col-right">
          <ClientPhone phone={c.phones[0]} />
          <section className="card cxp__sched" data-testid="client-tasks">
            <h2 className="card__title">Tâches et rappels</h2>
            <ClientSav c={c} setC={setC} />
            <ClientTasks c={c} setC={setC} />
            {c.blocked && <p className="cl-alert">Un dossier est « Bloqué au Pôle CEE ».</p>}
          </section>
          <section className="card cxp__fun" data-testid="client-aftersale">
            <h2 className="card__title">Suivi après signature</h2>
            <FriseList dossiers={c.dossiers_list} steps={c.aftersale} />
          </section>
        </div>
      </div>
    </section>
  );
}
